import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Divider, Form, Row, Col } from "antd";
import ReCAPTCHA from "react-google-recaptcha";
import { LoaderComponent, ButtonComponent, Text, InputComponent } from "@iiht/components";
import { LoginWallpaper, Mml } from "../../icons";
import { halfResponsiveSpan, responsiveGutter } from "../../constants/gridTypes";
import { CheckIfImageExists, getPaginationParams } from "../../utils";
import { ROUTES } from "../../constants/routesConstants";
import { toastNotification } from "../../utils/toast";
import { authTypes, customRuleCases, roleName, toasterFlags } from "../../constants/componentConstants";
import { applicationURL, translator } from "../../constants/actionTypes";
import { directLoginStorage, localStorageHelper } from "../../utils/storageHelper";
import { customValidator, maximumDigits, minimumDigits, Required, validEmail } from "../../utils/rules";
import {
  getResourcePermissions,
  loginUserAction,
  getUserNotificationListData,
  localUserLoginAction,
  verifyLocalUser,
  directLoginForAutoOnBoardingFalse,
  directLoginForAutoOnBoardingFalseIDP,
  demandLoginLocalUser,
  verifyLocalUserForDemandLogin,
  localUserSignupAction,
  localUserForgotPasswordAction,
  localUserResetPasswordAction,
} from "../../store/actions";
import { statusCode } from "../../constants/apis/apiStatus";
import AsteriskSymbol from "../common/AsteriskSymbol";
import NotFoundPage from "../common/NotFound";
import { getTenantUrl, getUserRole, setTokenDataInLocalStorage, setCustomTheme, checkIfObjectNotEmpty } from "../../utils/genericUtils";
import OidcAuthService from "../../config/OidcAuthService";
import { useRef } from "react";
import "./Login.scss";

const Login = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const recaptchaRef = React.useRef();
  const siteKey = process?.env?.REACT_APP_SITE_KEY_LOGIN || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"; //need to remove test key in future

  const {
    loginDetails,
    loginDetailsError,
    invalidTenantUrl,
    authTokenError,
    onboardingFalseData,
    authTokenSuccess,
    localUserTokenData,
    localUserLoginError,
    verifiedLocalUser,
    inValidLocalUser,
    localUserSignupSuccess,
    localUserSignupError,
    localUserForgotPasswordSuccess,
    localUserForgotPasswordError,
    localUserResetPasswordSuccess,
    localUserResetPasswordError,
  } = useSelector(({ userAuthentication }) => userAuthentication);
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [imageExist, setImageExist] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invalidTenant, setInvalidTenant] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [isSignupView, setIsSignupView] = useState(false);
  const [forgotPasswordView, setForgotPasswordView] = useState(false);
  const [newPasswordSettingView, setNewPasswordSettingView] = useState(false);
  const initialResetPasswordData = { password: "", confirm_password: "" };
  const initialSignupData = { first_name: "", last_name: "", email: "", password: "", confirm_password: "" };
  const [signUpSubmitData, setSignUpSubmitData] = useState(initialSignupData);
  const [resetPasswordData, setResetPasswordData] = useState(initialResetPasswordData);
  const [passwordError, setPasswordError] = useState("");
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  let oidc = useRef(null);

  useEffect(() => {
    setUserName(loginDetails?.data?.username);
    setPassword(loginDetails?.data?.password);
  }, [loginDetails])

  const loginForLocalUser = (captchaToken) => {
    let payload = {
      apiUrl: loginDetails?.data.auth_url,
      tenancyName: loginDetails?.data?.tenant_name,
      userNameOrEmailAddress: userName,
      password: password,
    };

    // check for direct login and portal login
    if (directLoginStorage.get("isDemandLogin")) {
      dispatch(demandLoginLocalUser(payload));
    } else {
      dispatch(localUserLoginAction(payload));
    }
  };

  useEffect(() => {
    let payload = {
      apiUrl: loginDetails?.data.auth_url,
      tenancyName: loginDetails?.data?.tenant_name,
      userNameOrEmailAddress: userName,
      password: password,
    };

    if (directLoginStorage.get("directLoginURLParams") && userName && password) {
      dispatch(demandLoginLocalUser(payload))
      setShowLoader(true);
    }

    return () => {
      setShowLoader(false);
    }
  }, [loginDetails, userName, password]);

  //dispatch for portal local user login
  const dispatchForPortalLocalUser = (captchaToken) => {
    dispatch(
      verifyLocalUser({
        tenant_url:
          window.location.pathname === "/"
            ? applicationURL
            : window?.document?.URL.includes("localhost")
              ? `${translator.baseURL}${localStorageHelper.get("tenantUrl")}`
              : getTenantUrl(),
        username: userName,
        captchaToken,
      })
    );
  };

  //dispatch for demand login local user
  const dispatchForDemandLoginLocalUser = (captchaToken) => {
    dispatch(
      verifyLocalUserForDemandLogin({
        tenant_url: directLoginStorage.get("demandLoginUrl"),
        username: userName,
        captchaToken,
      })
    );
  };

  const handleChange = (event) => {
    if (event?.target?.name === "username") setUserName(event?.target?.value);
    else if (event?.target?.name === "password") setPassword(event?.target?.value);
  };

  const handleSubmit = async () => {
    //Show loader inside login btn
    setLoading(true);

    const captchaToken = await recaptchaRef.current.executeAsync();
    recaptchaRef.current.reset();

    if (loginDetails?.data?.auth_type === authTypes.SAML) {
      if (loginDetails?.data?.is_auto_onboarding) {
        localStorageHelper.set("sessionId", loginDetails?.data?.sessionid);
        oidc = new OidcAuthService(loginDetails?.data?.auth_url, loginDetails?.data?.tenant_name);
        oidc.startAuthentication();
      } else {
        if (userName === "") {
          toastNotification(toasterFlags.ERROR, t("common.emptyUsername"));
          setLoading(false);
          return;
        }
        if (directLoginStorage.get("isDemandLogin")) {
          dispatch(directLoginForAutoOnBoardingFalseIDP({ tenant_url: loginDetails?.data?.tenant_url, username: userName }));
        } else {
          dispatch(loginUserAction({ tenant_url: loginDetails?.data?.tenant_url, username: userName }));
        }
      }
    }

    // check username and password for local user
    if (loginDetails?.data?.auth_type === authTypes.localUser) {
      if (userName === "") {
        toastNotification(toasterFlags.ERROR, t("common.emptyUsername"));
        setLoading(false);
        return;
      }
      if (password === "") {
        toastNotification(toasterFlags.ERROR, t("common.emptyPassword"));
        setLoading(false);
        return;
      }

      // if onboarding is true then call login function else verify user first then call login for local user
      if (loginDetails?.data?.is_auto_onboarding) {
        loginForLocalUser(captchaToken);
      } else {
        directLoginStorage.get("isDemandLogin") ? dispatchForDemandLoginLocalUser(captchaToken) : dispatchForPortalLocalUser(captchaToken);
      }
    }

    if (loginDetails?.data?.auth_type === authTypes?.azuerAD) {
      if (loginDetails?.data?.is_auto_onboarding) {
        localStorageHelper.set("sessionId", loginDetails?.data?.sessionid);
        window.open(loginDetails?.data?.auth_url, "_self");
      } else {
        if (userName === "") {
          toastNotification(toasterFlags.ERROR, t("common.emptyUsername"));
          setLoading(false);
          return;
        }
        if (directLoginStorage.get("isDemandLogin")) {
          dispatch(directLoginForAutoOnBoardingFalse({ tenant_url: loginDetails?.data?.tenant_url, username: userName }));
        } else {
          dispatch(loginUserAction({ tenant_url: loginDetails?.data?.tenant_url, username: userName }));
        }
      }
    }
  };

  const getDashboardData = () => {
    if (directLoginStorage.get("isDemandLogin")) {
      history.push({
        pathname: ROUTES.VM_CONNECT,
      });
    } else {
      dispatch(
        getResourcePermissions({
          roles: JSON.parse(localStorageHelper.get("role_name")),
          tenant_id: localStorageHelper.get("localTenantId")
        })
      );
      dispatch(getUserNotificationListData(getPaginationParams(6, 1, "-created_at")));

      const roleList = getUserRole();
      const is_portal_login = JSON.parse(localStorageHelper.get("is_portal_login"));
      const protectedRoute = directLoginStorage.get("protectedUrl");
      directLoginStorage.set("protectedUrl", "");
      if (protectedRoute) {
        history.push({
          pathname: protectedRoute,
        });
      } else {
        const isMentor = JSON.parse(localStorageHelper.get("is_mentor") || "false");
        if ((!is_portal_login && roleList === roleName?.tenantUser) || isMentor) {
          history.push({
            pathname: ROUTES.WORKSPACE_MANAGER,
          });
        } else {
          history.push({
            pathname: ROUTES.DASHBOARD,
          });
        }
      }
    }
  };

  useEffect(() => {
    if (onboardingFalseData?.data && onboardingFalseData?.data?.auth_type === authTypes.SAML) {
      oidc = new OidcAuthService(onboardingFalseData?.data?.auth_url, onboardingFalseData?.data?.tenant_name);
      oidc.startAuthentication();
    }
    if (onboardingFalseData) {
      localStorageHelper.set("sessionId", onboardingFalseData?.data?.sessionid);
      window.open(onboardingFalseData?.data?.auth_url, "_self");
    }
  }, [onboardingFalseData]);

  useEffect(() => {
    if (loginDetails?.status === statusCode.Ok200) {
      if (loginDetails?.data?.logo_path) {
        // check logo path if undefined set empty string
        let logoPath = loginDetails?.data?.logo_path ? loginDetails?.data?.logo_path : "";
        const isImageExist = CheckIfImageExists(
          (window?.location?.origin &&
            !window?.location?.origin?.includes("localhost") &&
            `${window?.location?.origin?.toString()}${loginDetails?.data?.logo_path}`) ||
          `${translator?.baseURL}${logoPath}`
        );
        isImageExist.then((response) => {
          setImageExist(response);
          if (response) {
            localStorageHelper.set("logo_path", loginDetails?.data?.logo_path);
          }
        });
      }
    }
  }, [loginDetails]);

  useEffect(() => {
    if (authTokenError.status === statusCode.BadRequest400) {
      history.push(ROUTES.TENANT_DETAILS_ERROR);
    }
    if (loginDetailsError?.status === statusCode.BadRequest400) {
      setUserName("");
      setLoading(false);
    }
    if (invalidTenantUrl?.status === statusCode.BadRequest400) {
      setInvalidTenant(true);
    }
    //* Error handling for local user
    if (localUserLoginError?.status === statusCode.BadRequest400) {
      setUserName("");
      setPassword("");
      form.setFieldsValue(initialSignupData);
      setLoading(false);
    }
    if (localUserSignupError?.status === statusCode.BadRequest400) {
      setLoading(false);
    }
    setLoading(false);
  }, [
    loginDetailsError,
    authTokenError,
    invalidTenantUrl,
    localUserLoginError,
    localUserSignupError,
    localUserForgotPasswordError,
    localUserResetPasswordError,
  ]);

  useEffect(() => {
    if (localUserTokenData?.status === statusCode.Ok200 && !localStorageHelper.get("accessToken")) {
      let tokenData = {
        ...localUserTokenData?.data,
        access_token: localUserTokenData?.data?.id_token,
        refresh_token: localUserTokenData?.data?.refresh,
        tenantId: localUserTokenData?.data?.localTenantId,
      };
      setTokenDataInLocalStorage(tokenData, authTypes?.localUser);
      getDashboardData();
    }
    if (authTokenSuccess && localStorageHelper.get("accessToken")) {
      setTokenDataInLocalStorage(authTokenSuccess?.data, "");
      getDashboardData();
    }
    const role = JSON.parse(localStorageHelper.get("role_name"));
    if (role?.[0] !== roleName?.superadmin && role?.[0] !== roleName?.csmadmin && role?.[0] !== roleName?.labadmin) {
      setCustomTheme(localUserTokenData?.data);
    }
  }, [authTokenSuccess, localUserTokenData]);

  useEffect(() => {
    if (verifiedLocalUser?.status === statusCode.Ok200) {
      loginForLocalUser();
    }
    if (inValidLocalUser?.status === statusCode.BadRequest400) {
      //Add validation for invalid user
    }
  }, [verifiedLocalUser, inValidLocalUser]);

  useEffect(() => {
    // sessionStorage.setItem("portalLogin", true);
  }, []);

  useEffect(() => {
    if (checkIfObjectNotEmpty(localUserSignupSuccess)) {
      signUpViewHandler();
      setLoading(false);
    }
  }, [localUserSignupSuccess]);

  useEffect(() => {
    if (checkIfObjectNotEmpty(localUserForgotPasswordSuccess)) {
      setNewPasswordSettingView(true);
      setForgotPasswordView(false);
      setLoading(false);
    }
  }, [localUserForgotPasswordSuccess]);

  useEffect(() => {
    if (checkIfObjectNotEmpty(localUserResetPasswordSuccess)) {
      setNewPasswordSettingView(false);
      setIsSignupView(false);
      setLoading(false);
      resetForm();
    }
    setLoading(false);
  }, [localUserResetPasswordSuccess]);

  const handleFormChange = (e, fieldName) => {
    setPasswordError("");
    form.setFieldsValue({ [fieldName]: e });
    setSignUpSubmitData({ ...signUpSubmitData, [fieldName]: e });
  };

  const handlePasswordFormChange = (e, fieldName) => {
    setPasswordError("");
    passwordForm.setFieldsValue({ [fieldName]: e });
    setResetPasswordData({ ...resetPasswordData, [fieldName]: e });
  };

  const resetForm = () => {
    form.setFieldsValue(initialSignupData);
    passwordForm.setFieldsValue(initialResetPasswordData);
    setSignUpSubmitData(initialSignupData);
    setResetPasswordData(initialResetPasswordData);
    setUserName("");
    setPassword("");
    setPasswordError("");
    localStorageHelper.removeItem("resetUrl");
  };

  const signUpViewHandler = () => {
    setIsSignupView(!isSignupView);
    setForgotPasswordView(false);
    resetForm();
  };

  const handleForgotPassword = () => {
    if (userName === "") {
      toastNotification(toasterFlags.ERROR, t("common.emptyUsername"));
      setLoading(false);
      return;
    }
    setLoading(true);
    dispatch(
      localUserForgotPasswordAction({
        email: userName,
      })
    );
  };

  const resetPasswordHandler = () => {
    if (resetPasswordData.password !== resetPasswordData.confirm_password) {
      setPasswordError(t("LoginManagement.validatePassword"));
      return;
    }
    setLoading(true);
    dispatch(
      localUserResetPasswordAction({
        password: resetPasswordData.password,
        confirm_password: resetPasswordData.confirm_password,
        reset_url: localStorageHelper.get("resetUrl"),
      })
    );
  };

  const signUpHandler = () => {
    if (signUpSubmitData.password !== signUpSubmitData.confirm_password) {
      setPasswordError(t("LoginManagement.validatePassword"));
      return;
    }
    setLoading(true);
    dispatch(
      localUserSignupAction({
        first_name: signUpSubmitData.first_name,
        last_name: signUpSubmitData.last_name,
        email: signUpSubmitData.email,
        password: signUpSubmitData.password,
        confirm_password: signUpSubmitData.confirm_password,
        tenant: localStorageHelper.get("localTenantId"),
      })
    );
  };

  const getUserNameField = () => {
    return (
      <>
        <div className={"body-login-card"}>
          <div className={"usrnm mb2"}>
            <label>
              {t("LoginManagement.username")}
              <AsteriskSymbol />
            </label>
            <input
              type="text"
              name="username"
              className={"form-input-cls"}
              placeholder={t("resourceProvider.typeUsername")}
              id="username"
              onChange={handleChange}
              value={userName}
            />
          </div>
          <div className={"login-button"}>
            {forgotPasswordView ? (
              <ButtonComponent className="qa-login-btn" onClick={handleForgotPassword} loading={loading}>
                {t("LoginManagement.continue")}
              </ButtonComponent>
            ) : (
              <ButtonComponent className="qa-login-btn" onClick={handleSubmit} loading={loading}>
                {t("LoginManagement.login")}
              </ButtonComponent>
            )}
          </div>
          <Divider className={"login-divider"} />
        </div>
      </>
    );
  };

  const getLoginButton = () => {
    return (
      <>
        <div className={"body-login-card"}>
          <div className={"auto-login mb2"}>
            <div className={"autoOnboard-button"}>
              <ButtonComponent className="qa-login-btn" onClick={handleSubmit}>
                {t("LoginManagement.login")}
              </ButtonComponent>
            </div>
            <Divider className={"login-divider"} />
          </div>
        </div>
      </>
    );
  };

  const getLocalUserLoginSignup = () => {
    return (
      <>
        {newPasswordSettingView ? (
          <div className={"body-login-card"}>
            <Form
              layout="vertical"
              form={passwordForm}
              data-testid="group-select-component"
              onFinish={resetPasswordHandler}
              requiredMark={false}
              colon={false}
              initialValues={resetPasswordData}
              className="w-100"
            >
              <Row gutter={[responsiveGutter, 16]}>
                <Col {...responsiveGutter}>
                  <Form.Item
                    name="password"
                    label={
                      <>
                        {t("LoginManagement.createPassword")}
                        <AsteriskSymbol />
                      </>
                    }
                    rules={[
                      Required(`${t("validations.enter")}${t("LoginManagement.password")}`),
                      minimumDigits(t("common.minimumEight"), 8),
                      maximumDigits(t("common.maxSixteenChars"), 16),
                      customValidator("", t("common.validPassword"), customRuleCases.password),
                    ]}
                  >
                    <InputComponent
                      name="password"
                      type="password"
                      value={resetPasswordData}
                      onChange={(e) => {
                        handlePasswordFormChange(e.target.value, "password");
                      }}
                      placeholder={`${t("common.enter")} ${t("LoginManagement.password")}`}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[responsiveGutter, 16]}>
                <Col {...responsiveGutter}>
                  <Form.Item
                    name="confirm_password"
                    rules={[Required(`${t("validations.enter")}${t("LoginManagement.password")}`)]}
                    label={
                      <>
                        {t("LoginManagement.confirmPassword")}
                        <AsteriskSymbol />
                      </>
                    }
                  >
                    <InputComponent
                      name="confirm_password"
                      type="password"
                      value={resetPasswordData}
                      onChange={(e) => {
                        handlePasswordFormChange(e.target.value, "confirm_password");
                      }}
                      placeholder={`${t("common.enter")} ${t("LoginManagement.confirmPassword")}`}
                    />
                  </Form.Item>
                  {passwordError && <p className="password-error">{passwordError}</p>}
                </Col>
              </Row>
              <Form.Item>
                <div className="login-button">
                  <ButtonComponent className="qa-login-btn" loading={loading} type="primary" htmlType="submit">
                    {t("LoginManagement.submit")}
                  </ButtonComponent>
                </div>
              </Form.Item>
            </Form>
            <Divider className={"login-divider"} />
          </div>
        ) : forgotPasswordView ? (
          getUserNameField()
        ) : isSignupView ? (
          <div className={"body-login-card"}>
            <Form
              layout="vertical"
              form={form}
              data-testid="group-select-component"
              onFinish={signUpHandler}
              requiredMark={false}
              colon={false}
              initialValues={signUpSubmitData}
            >
              <Row gutter={[responsiveGutter, 16]}>
                <Col {...halfResponsiveSpan}>
                  <Form.Item
                    name="first_name"
                    rules={[Required(`${t("validations.enter")}${t("firstName")}`)]}
                    label={
                      <>
                        {t("firstName")}
                        <AsteriskSymbol />
                      </>
                    }
                  >
                    <InputComponent
                      name="first_name"
                      value={signUpSubmitData}
                      onChange={(e) => {
                        handleFormChange(e.target.value, "first_name");
                      }}
                      placeholder={`${t("common.enter")} ${t("firstName")}`}
                    />
                  </Form.Item>
                </Col>
                <Col {...halfResponsiveSpan}>
                  <Form.Item
                    name="last_name"
                    rules={[Required(`${t("validations.enter")}${t("lastName")}`)]}
                    label={
                      <>
                        {t("lastName")}
                        <AsteriskSymbol />
                      </>
                    }
                  >
                    <InputComponent
                      name="last_name"
                      value={signUpSubmitData}
                      onChange={(e) => {
                        handleFormChange(e.target.value, "last_name");
                      }}
                      placeholder={`${t("common.enter")} ${t("lastName")}`}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[responsiveGutter, 16]}>
                <Col {...responsiveGutter}>
                  <Form.Item
                    name="email"
                    rules={[
                      Required(`${t("validations.enter")}${t("LoginManagement.username")}`),
                      validEmail(`${t("validations.enterValid")} ${t("LoginManagement.username")}`),
                    ]}
                    label={
                      <>
                        {t("LoginManagement.emailId")}
                        <AsteriskSymbol />
                      </>
                    }
                  >
                    <InputComponent
                      name="email"
                      value={signUpSubmitData}
                      onChange={(e) => {
                        handleFormChange(e.target.value, "email");
                      }}
                      placeholder={`${t("common.enter")} ${t("LoginManagement.username")}`}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[responsiveGutter, 16]}>
                <Col {...responsiveGutter}>
                  <Form.Item
                    name="password"
                    label={
                      <>
                        {t("LoginManagement.createPassword")}
                        <AsteriskSymbol />
                      </>
                    }
                    rules={[
                      Required(`${t("validations.enter")}${t("LoginManagement.password")}`),
                      minimumDigits(t("common.minimumEight"), 8),
                      maximumDigits(t("common.maxSixteenChars"), 16),
                      customValidator("", t("common.validPassword"), customRuleCases.password),
                    ]}
                  >
                    <InputComponent
                      name="password"
                      type="password"
                      value={signUpSubmitData}
                      onChange={(e) => {
                        handleFormChange(e.target.value, "password");
                      }}
                      placeholder={`${t("common.enter")} ${t("LoginManagement.password")}`}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[responsiveGutter, 16]}>
                <Col {...responsiveGutter}>
                  <Form.Item
                    name="confirm_password"
                    rules={[Required(`${t("validations.enter")}${t("LoginManagement.password")}`)]}
                    label={
                      <>
                        {t("LoginManagement.confirmPassword")}
                        <AsteriskSymbol />
                      </>
                    }
                  >
                    <InputComponent
                      name="confirm_password"
                      type="password"
                      value={signUpSubmitData}
                      onChange={(e) => {
                        handleFormChange(e.target.value, "confirm_password");
                      }}
                      placeholder={`${t("common.enter")} ${t("LoginManagement.confirmPassword")}`}
                    />
                  </Form.Item>
                  {passwordError && <p className="password-error">{passwordError}</p>}
                </Col>
              </Row>
              <Form.Item>
                <div className="login-button">
                  <ButtonComponent className="qa-login-btn" loading={loading} type="primary" htmlType="submit">
                    {t("LoginManagement.signUp")}
                  </ButtonComponent>
                </div>
              </Form.Item>
            </Form>
            <Divider className={"login-divider"} />
            <div className={"login-button"}>
              <Text className="font-wt-bold signup-text">
                {t("LoginManagement.alreadyUser")}{" "}
                <Text className="underline pointer-cursor" onClick={signUpViewHandler}>
                  {t("LoginManagement.login")}
                </Text>
              </Text>
            </div>
          </div>
        ) : (
          <div className={"body-login-card"}>
            <div className={"usrnm mb2"}>
              <label>{t("LoginManagement.username")}</label>
              <input
                type="text"
                name="username"
                className={"form-input-cls"}
                placeholder={t("resourceProvider.typeUsername")}
                id="username"
                onChange={handleChange}
                value={userName}
              />
            </div>
            <div className={"usrnm mt6"}>
              <span className="display-flex justify-space-between">
                <label>{t("LoginManagement.password")}</label>
                <label className="forgotPassword pointer-cursor" onClick={() => setForgotPasswordView(true)}>
                  {t("LoginManagement.forgotPassword")}
                </label>
              </span>
              <input
                type="password"
                name="password"
                className={"form-input-cls"}
                placeholder={t("resourceProvider.typePassword")}
                id="password"
                onChange={handleChange}
                value={password}
              />
            </div>
            <div className={"login-button"}>
              <ButtonComponent className="qa-login-btn" onClick={handleSubmit} loading={loading}>
                {t("LoginManagement.login")}
              </ButtonComponent>
            </div>
            <Divider className={"login-divider"} />
            {loginDetails?.data?.is_mark_for_b2c && (
              <div className={"login-button"}>
                <Text className="font-wt-bold signup-text">
                  {t("LoginManagement.firstTimeUser")}{" "}
                  <Text className="underline pointer-cursor" onClick={signUpViewHandler}>
                    {t("LoginManagement.signUp")}
                  </Text>
                </Text>
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  const getLoginFields = () => {
    if (loginDetails?.data?.auth_type === authTypes.localUser) {
      return getLocalUserLoginSignup();
    } else {
      return loginDetails?.data?.is_auto_onboarding ? getLoginButton() : getUserNameField();
    }
  };

  const getLoginPage = () => {
    return loginDetails?.status === statusCode.Ok200 ? (
      <div className={"login-background"}>
        <Row className={"login-row"}>
          <Col {...halfResponsiveSpan} className={"hide-page-1"}>
            <div className="login-page split">
              <div className={"logo-container"}>
                {(loginDetails?.data?.logo_path && imageExist && (
                  <img
                    src={
                      (window?.location?.origin &&
                        !window?.location?.origin?.includes("localhost") &&
                        `${window?.location?.origin?.toString()}${loginDetails?.data?.logo_path}`) ||
                      `${translator?.baseURL}${loginDetails?.data?.logo_path}`
                    }
                    alt="logo"
                    className={"mml-logo"}
                  />
                )) || <Mml className="mml-logo" />}
              </div>
              <div className="login-wrapper">
                <div className={"mml-title"}>
                  <div className={"welcome-to-text"}>{t("LoginManagement.welcomeTo")}</div>
                  <div className={"mml-text"}>{t("LoginManagement.mml")}</div>
                </div>
                <LoginWallpaper className={"login-center-icon"} />
              </div>
            </div>
          </Col>
          <Col {...halfResponsiveSpan}>
            <div className={"login-card split right"}>
              <div className={"logo-container"}>{loginDetails?.data?.logo_path && imageExist && <Mml className={"mml-logo-page"} />}</div>
              <div className={"header-login-card"}>
                {newPasswordSettingView ? (
                  <p className={"login-heading"}>{t("LoginManagement.createNewPassword")}</p>
                ) : forgotPasswordView ? (
                  <p className={"login-heading"}>{t("LoginManagement.passwordAssistance")}</p>
                ) : isSignupView ? (
                  <p className={"login-heading"}>{t("LoginManagement.signUp")}</p>
                ) : (
                  <p className={"login-heading"}>{t("LoginManagement.login")}</p>
                )}
                <Divider className={"header-divider"} />
              </div>
              {getLoginFields()}
              <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={siteKey} />
              <div className={"mml-iiht"}>{t("mmlIiht")}</div>
            </div>
          </Col>
        </Row>
      </div>
    ) : (
      <LoaderComponent spinning={true} isBackdrop={true} />
    );
  };

  return (
    <>
      {invalidTenant ? (
        <>
          <NotFoundPage invalidTenant={invalidTenant} />
        </>
      ) : showLoader ? (
        <LoaderComponent spinning isBackdrop />
      ) : (
        getLoginPage()
      )}
    </>
  );
};

export default Login;
